import type { AvatarProps } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import { Avatar } from "@productlane/ui"

import { useIsMemberOnline } from "@/lib/states/presence-state"

type DotSize = "xs" | "sm" | "md" | "lg"

const DOT_SIZE: Record<DotSize, string> = {
	xs: "h-1.5 w-1.5 ring-2",
	sm: "h-2 w-2 ring-2",
	md: "h-2.5 w-2.5 ring-2",
	lg: "h-3.5 w-3.5 ring-[3px]",
}

export function PresenceDot({
	isOnline,
	size = "md",
	className,
}: {
	isOnline: boolean
	size?: DotSize
	className?: string
}) {
	return (
		<span
			aria-label={isOnline ? "Online" : "Offline"}
			className={cn(
				"flex-none rounded-full ring-white dark:ring-gray-950",
				DOT_SIZE[size],
				isOnline ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600",
				className,
			)}
		/>
	)
}

/**
 * `className` styles the wrapper, so a margin on it never drags the dot off the
 * avatar's corner.
 */
export function PresenceAvatar({
	userId,
	dotSize = "md",
	className,
	...avatarProps
}: AvatarProps & { userId: string | undefined; dotSize?: DotSize }) {
	const isOnline = useIsMemberOnline(userId)
	const isRound = (avatarProps.roundness ?? "full") === "full"

	return (
		<span className={cn("relative inline-flex flex-none", className)}>
			<Avatar {...avatarProps} />
			<PresenceDot
				isOnline={isOnline}
				size={dotSize}
				className={
					isRound
						? // A circle's box corner sits outside the circle; 85.36% (1/2 + √2/4) is its edge at 45°.
							"absolute left-[85.36%] top-[85.36%] -translate-x-1/2 -translate-y-1/2"
						: "absolute -bottom-0.5 -right-0.5"
				}
			/>
		</span>
	)
}
